import type React from "react";
import type { Omittable } from "@salestrak/ui";

import {
  Button,
  ButtonClear,
  ButtonExpansion,
  Icon,
  Text,
  type ButtonProps,
  type IconName,
  type IconSize,
} from "@salestrak/ui";

type FilterToggleSize = "xs" | "sm" | "default" | "lg";

type FilterToggleProps = Omit<
  ButtonProps,
  "children" | "size" | "variant" | "expanded" | "loading" | "shape"
> & Omittable<{
  icon: IconName;
  /** Selector-trigger label, shown as-is (a date preset, a slice). On the
   *  plain filter chip it is the singular noun the count pluralizes
   *  ("Filter" → "3 Filters"). */
  label?: string;
  /** Active-filter count. Zero keeps the chip collapsed to its icon; a
   *  positive count expands it to the pluralized label ("1 Filter"). */
  count?: number;
  /** Defaults to `count > 0`. */
  selected?: boolean;
  /** Renders the clear (×) affordance while selected. */
  onClear?: () => void;
  size?: FilterToggleSize;
  iconSize?: IconSize;
  /** Tint for the icon face — for fixed-color backdrops (map controls) where
   *  the theme foreground doesn't match sibling icons. */
  iconTintColor?: string;
  /** Stadium pill (default) or the squared chip. */
  shape?: "rounded" | "pill";
  /**
   * Structural variant of the OFF (unselected) state. `ghost` is the borderless
   * toolbar chip; `outline` gives it a resting border.
   */
  offVariant?: "ghost" | "outline";
  ref?: React.Ref<HTMLButtonElement>;
}>;

/**
 * The standard toolbar filter chip: icon-only at rest, expanding to the
 * pluralized count ("1 Filter", "3 Filters") with a hover-revealed clear
 * overlay while filters are active. Pass `label` without `count` for
 * selector-trigger mode (a date preset, a slice chip), where the label IS
 * the selection and stays on the face. Composes as a `render` child of
 * Menu/Popover/Select/EntitySelector triggers.
 */
export function FilterToggle({
  icon,
  label,
  count,
  selected,
  onClear,
  size = "default",
  iconSize = "sm",
  iconTintColor,
  shape = "pill",
  offVariant = "ghost",
  ref,
  ...props
}: FilterToggleProps) {
  const isSelected = selected ?? (count !== undefined && count > 0);
  const showCount = count !== undefined && count > 0;
  const showClear = isSelected && !!onClear;
  const noun = label ?? "Filter";
  const countLabel = showCount
    ? `${count} ${count === 1 ? noun : `${noun}s`}`
    : null;

  return (
    <Button
      ref={ref}
      size={size}
      // Selected always reads as the theme-colored outline; unselected
      // follows `offVariant`.
      variant={
        isSelected
          ? "primary-outline"
          : offVariant === "outline"
            ? "outline"
            : "ghost"
      }
      aria-pressed={isSelected}
      expanded={isSelected && showCount}
      shape={shape === "pill" ? "pill" : undefined}
      // Icon-only mode has no visible text to name the control.
      aria-label={label ? undefined : "Filters"}
      {...props}
    >
      <Icon
        name={icon}
        size={iconSize}
        // A resolved (non-token) color from a fixed-color backdrop — the only
        // way to paint lucide's currentColor from a runtime value.
        style={iconTintColor ? { color: iconTintColor } : undefined}
      />
      {label && count === undefined ? (
        <Text size="sm" truncate={1}>
          {label}
        </Text>
      ) : null}
      <ButtonExpansion>
        {countLabel ? (
          <Text size="sm" truncate={1}>
            {countLabel}
          </Text>
        ) : null}
      </ButtonExpansion>
      {showClear ? (
        <ButtonClear aria-label="Clear selection" onClick={() => onClear()} />
      ) : null}
    </Button>
  );
}

export type { FilterToggleProps, FilterToggleSize };
